/**
 * Notification API Service
 * All notification-related API calls
 */

import { pingTaxCloudinstalltionandremovals } from "components/specialFeeTaxCloud";
import { getAuthCookie } from "../utils/authCookies";

const API_BASE = process.env.REACT_APP_API_URL || "";

/** Resolve a usable foundation global_user_id for chat notification routes. */
export function resolveNotificationGlobalUserId(
  globalUserId?: string | null
): string {
  const fromArg = String(globalUserId ?? "").trim();
  if (fromArg && fromArg !== "undefined" && fromArg !== "null") {
    return fromArg;
  }
  const fromCookie = String(getAuthCookie("globalUserId") ?? "").trim();
  if (fromCookie && fromCookie !== "undefined" && fromCookie !== "null") {
    return fromCookie;
  }
  return "";
}

/**
 * Notification recipient type to license type mapping
 * Maps notification recipient types to their corresponding license types for env variable checking
 */
const NOTIFICATION_RECIPIENT_TYPE_TO_LICENSE_TYPE: Record<string, string> = {
  brokerage_admin: "BROKERAGE_FIRM",
  brokerage_team_member: "BROKERAGE_TEAM_MEMBER",
  brokerage_team: "BROKERAGE_TEAM_MEMBER",
  signage_admin: "SIGNAGE_FIRM",
  team_member: "SIGNAGE_TEAM_MEMBER",
  signage_team: "SIGNAGE_TEAM_MEMBER",
  signage: "SIGNAGE_FIRM",
  agent: "AGENT",
};

/**
 * License type to environment variable mapping
 */
const LICENSE_TYPE_ENV_MAP: Record<string, string> = {
  SIGNAGE_FIRM: "REACT_APP_ENABLE_EMAIL_SIGNAGE_FIRM",
  SIGNAGE_TEAM_MEMBER: "REACT_APP_ENABLE_EMAIL_SIGNAGE_TEAM_MEMBER",
  AGENT: "REACT_APP_ENABLE_EMAIL_AGENT",
  BROKERAGE_AGENT: "REACT_APP_ENABLE_EMAIL_BROKERAGE_AGENT",
  BROKERAGE_FIRM: "REACT_APP_ENABLE_EMAIL_BROKERAGE_FIRM",
  BROKERAGE_TEAM_MEMBER: "REACT_APP_ENABLE_EMAIL_BROKERAGE_TEAM_MEMBER",
};

/**
 * Helper function to check if a license type is enabled
 */
function isLicenseTypeEnabled(licenseType: string): boolean {
  // Master switch: if REACT_APP_ENABLE_ALL_EMAIL is false, disable all notifications/emails
  const allEmailEnabled = process.env.REACT_APP_ENABLE_ALL_EMAIL;
  if (allEmailEnabled !== undefined && allEmailEnabled !== null) {
    const normalizedAllEmail = allEmailEnabled.trim().toLowerCase();
    if (normalizedAllEmail === "false" || normalizedAllEmail === "0" || normalizedAllEmail === "no") {
      return false;
    }
  }
  
  const envVarName = LICENSE_TYPE_ENV_MAP[licenseType];
  if (!envVarName) return true;
  
  const envValue = process.env[envVarName];
  if (envValue === undefined || envValue === null) return true;
  
  const normalizedValue = envValue.trim().toLowerCase();
  return normalizedValue === "true" || normalizedValue === "1" || normalizedValue === "yes";
}

/**
 * Helper function to check if a notification recipient type is enabled
 */
function isNotificationRecipientTypeEnabled(recipientType: string): boolean {
  const licenseType = NOTIFICATION_RECIPIENT_TYPE_TO_LICENSE_TYPE[recipientType];
  console.log("Checking notification recipient type:", recipientType, "mapped license type:", licenseType);
  if (!licenseType) return true;
  
  if (recipientType === "agent") {
    return isLicenseTypeEnabled("AGENT") || isLicenseTypeEnabled("BROKERAGE_AGENT");
  }
  
  return isLicenseTypeEnabled(licenseType);
}

export interface CreateNotificationPayload {
  title: string;
  text: string;
  text_color: string;
}

export interface CreateNotificationResponse {
  created_at: string;
  notification_id: string;
  status: string;
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface NotificationRow {
  _id?: string; // API returns _id
  notification_id?: string; // Some APIs might use notification_id
  created_at: string;
  status?: string;
  title: string;
  text: string;
  text_color: string;
  seen?: boolean;
  user_id?: string;
  message_id?: string | null;
  thread_id?: string | null;
  created_by?: {
    id: string;
    name: string;
  };
  sender_id?: string;
  action?: string;
  related_resource_type?: string;
  related_resource_ids?: string[];
  metadata?: Record<string, unknown>;
}

export interface NotificationDatatableResponse {
  limit: number;
  page: number;
  rows: NotificationRow[];
  total: number;
}

export interface MarkSeenResponse {
  status: string;
}

export interface MarkBatchSeenResponse {
  count: number;
  status: string;
}

export interface DeleteSelectedResponse {
  count?: number;
  status?: string;
}

/**
 * 1️⃣ Create notification
 * 
 * @param globalUserId - Recipient global_user_id (foundation id)
 * @param payload - The notification payload (title, text, text_color)
 * @param token - Authentication token
 * @param recipientType - Optional recipient type for filtering (e.g., "brokerage_admin", "agent", "signage_admin")
 *                        If provided and disabled via env variable, notification will be skipped
 * @returns Promise that resolves to the notification response or null if skipped
 */
export async function createNotification(
  globalUserId: string,
  payload: CreateNotificationPayload,
  token: string,
  recipientType?: string
): Promise<CreateNotificationResponse | null> {
  // Master switch: if REACT_APP_ENABLE_ALL_EMAIL is false, disable all notifications
  const allEmailEnabled = process.env.REACT_APP_ENABLE_ALL_EMAIL;
  if (allEmailEnabled !== undefined && allEmailEnabled !== null) {
    const normalizedAllEmail = allEmailEnabled.trim().toLowerCase();
    if (normalizedAllEmail === "false" || normalizedAllEmail === "0" || normalizedAllEmail === "no") {
      console.log(`⚠️ Notification skipped - master switch REACT_APP_ENABLE_ALL_EMAIL is disabled`);
      return null;
    }
  }

  // If recipientType is provided, check if it's enabled
  if (recipientType && !isNotificationRecipientTypeEnabled(recipientType)) {
    console.log(`⚠️ Notification skipped for recipient type "${recipientType}" (disabled via env variable)`);
    return null; // Return null to indicate notification was skipped
  }

  const resolvedGlobalUserId = resolveNotificationGlobalUserId(globalUserId);
  if (!resolvedGlobalUserId) {
    console.warn(
      "⚠️ Notification skipped - missing global_user_id (legacy userId cookie is not used)"
    );
    return null;
  }

  const response = await fetch(
    `${API_BASE}/chat/notifications/global-users/${encodeURIComponent(resolvedGlobalUserId)}/create`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to create notification: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 2️⃣ Get unread notifications count
 */
export async function getUnreadCount(
  globalUserId: string,
  token: string
): Promise<UnreadCountResponse> {
  const resolvedGlobalUserId = resolveNotificationGlobalUserId(globalUserId);
  if (!resolvedGlobalUserId) {
    throw new Error("global_user_id is required for unread notification count");
  }
  const response = await fetch(
    `${API_BASE}/chat/notifications/global-users/${encodeURIComponent(resolvedGlobalUserId)}/unread-count`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to get unread count: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 3️⃣ Notifications datatable
 */
export async function getNotificationsDatatable(
  globalUserId: string,
  params: {
    page?: number;
    limit?: number;
    seen?: "seen" | "unseen" | "all";
    sort_field?: string;
    sort_dir?: 1 | -1;
    search?: string;
  },
  token: string
): Promise<NotificationDatatableResponse> {
  const queryParams = new URLSearchParams();
  if (params.page !== undefined) queryParams.append("page", String(params.page));
  if (params.limit !== undefined) queryParams.append("limit", String(params.limit));
  if (params.seen) queryParams.append("seen", params.seen);
  if (params.sort_field) queryParams.append("sort_field", params.sort_field);
  if (params.sort_dir !== undefined) queryParams.append("sort_dir", String(params.sort_dir));
  if (params.search) queryParams.append("search", params.search);

  const url = `${API_BASE}/chat/notifications/global-users/${globalUserId}/datatable${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to get notifications: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 4️⃣ Mark ALL notifications as seen
 */
export async function markAllAsSeen(
  globalUserId: string,
  token: string
): Promise<MarkSeenResponse> {
  const response = await fetch(
    `${API_BASE}/chat/notifications/global-users/${globalUserId}/seen`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to mark all as seen: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 5️⃣ Mark SELECTED notifications as seen (batch)
 */
export async function markSelectedAsSeen(
  globalUserId: string,
  notificationIds: string[],
  token: string
): Promise<MarkBatchSeenResponse> {
  const response = await fetch(
    `${API_BASE}/chat/notifications/global-users/${globalUserId}/seen/batch`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        notification_ids: notificationIds,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to mark selected as seen: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 6️⃣ Mark SINGLE notification as seen
 */
export async function markSingleAsSeen(
  globalUserId: string,
  notificationId: string,
  token: string
): Promise<MarkSeenResponse> {
  // Validate inputs
  if (!globalUserId || !notificationId || !token) {
    throw new Error(
      `Missing required parameters: globalUserId=${!!globalUserId}, notificationId=${!!notificationId}, token=${!!token}`
    );
  }

  // Ensure notificationId is a valid string
  const cleanNotificationId = String(notificationId).trim();
  if (!cleanNotificationId || cleanNotificationId === "undefined" || cleanNotificationId === "null") {
    throw new Error(`Invalid notificationId: ${notificationId}`);
  }

  const url = `${API_BASE}/chat/notifications/global-users/${globalUserId}/${cleanNotificationId}/seen`;
  
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to mark as seen: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 7️⃣ Delete SINGLE notification
 */
export async function deleteSingleNotification(
  globalUserId: string,
  notificationId: string,
  token: string
): Promise<{ status?: string; deleted?: boolean }> {
  const response = await fetch(
    `${API_BASE}/chat/notifications/global-users/${globalUserId}/${notificationId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to delete notification: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 8️⃣ Delete SELECTED notifications
 */
export async function deleteSelectedNotifications(
  globalUserId: string,
  notificationIds: string[],
  token: string
): Promise<DeleteSelectedResponse> {
  const response = await fetch(
    `${API_BASE}/chat/notifications/global-users/${globalUserId}/delete-selected`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        notification_ids: notificationIds,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to delete selected notifications: ${response.statusText}`);
  }

  return response.json();
}

/**
 * 9️⃣ Delete ALL notifications
 */
export async function deleteAllNotifications(
  globalUserId: string,
  token: string
): Promise<{ status?: string; deleted?: boolean }> {
  const response = await fetch(
    `${API_BASE}/chat/notifications/global-users/${globalUserId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to delete all notifications: ${response.statusText}`);
  }

  return response.json();
}

